import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import {Card} from "react-bootstrap";
import { BASEAPIURL, token } from "../env.js";

export default function FacilityDetails() {
  const { state: rawFacility } = useLocation();

  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [extraFacilityData, setExtraFacilityData] = useState(null);
  const [error, setError] = useState(null);
  const [extraFacilitiesLoading, setExtraFacilitiesLoading] = useState(false); // New loading state

  const normalizeFacility = (raw) => {
    if (!raw || typeof raw !== "object") return null;
    let desc = raw.Facility_Description || raw.desc || "";
    try {
      if (typeof desc === "string" && desc.startsWith('"')) {
        desc = JSON.parse(desc);
      }
    } catch {
      desc = "No description available.";
    }
    return {
      title: raw.Facility_Name || raw.title || "Untitled",
      desc,
      desc2: raw.desc2 || "",
      images: raw.Facility_Image || raw.images || [],
      Facility_DesImage: raw.Facility_DesImage || "",
      branch: raw.branch || [],
      FacilityGroupID: raw.FacilityGroupID || raw.Facility_ID || "",
    };
  };

  const facility = normalizeFacility(rawFacility);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const fetchExtraContent = async () => {
      if (!facility?.FacilityGroupID) {
        // setError("No Facility Group ID provided."); // Optional: remove if not strictly an error
        return;
      }
      try {
        setExtraFacilitiesLoading(true); // Start loading
        setError(null); // Clear previous errors
        const res = await fetch(
          `${BASEAPIURL}/GetFacilityUIByFGrpID?FGrpID=${facility.FacilityGroupID}&Token=${encodeURIComponent(token)}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch facility data");
        const text = await res.text();
        const parsed = JSON.parse(text);
        // Handle potential structure of response
        const data = Array.isArray(parsed?.data) ? parsed.data : (parsed?.data ? [parsed.data] : []);
        setExtraFacilityData(data);
      } catch (err) {
        // Hide the error from console if it's a network/fetch error
        if (!err.message.includes("Failed to fetch")) {
          console.error("Error fetching extra facilities:", err);
        }
        // Don't set error message for fetch failures to hide the warning
        if (!err.message.includes("Failed to fetch")) {
          setError("Failed to load additional facility details.");
        }
        setExtraFacilityData([]); // Set to empty array on error to stop loading
      } finally {
        setExtraFacilitiesLoading(false); // Stop loading regardless of outcome
      }
    };
    fetchExtraContent();
  }, [facility?.FacilityGroupID]); // Added token dependency if it changes

  if (!facility) {
    return (
      <div className="text-center my-5">
        <h4>Facility not found</h4>
        <p>This page was accessed without valid facility data.</p>
        <button className="btn btn-primary mt-3" onClick={() => navigate("/")}>
          Go to Home
        </button>
      </div>
    );
  }

  const {
    title,
    desc,
    desc2,
    images,
    Facility_DesImage,
    branch,
    FacilityGroupID,
  } = facility;

  const getResponsiveHeight = () => {
    if (windowWidth < 480) return "35vh";
    if (windowWidth < 768) return "50vh";
    if (windowWidth < 1200) return "60vh";
    return "75vh";
  };

  const renderExtraFacilityCard = () => {
    // Show spinner while loading
    if (extraFacilitiesLoading) {
      return (
        <div className="row p-4 g-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div className="col-md-3 col-sm-6" key={i}>
            <div className="card shadow-sm border-0">
              {/* 🔹 Image Placeholder */}
              <div className="placeholder-glow">
                <div
                  className="card-img-top bg-secondary placeholder"
                  style={{ height: "200px" }}
                ></div>
              </div>

              {/* 🔹 Text Placeholder */}
              <div className="card-body">
                <h5 className="card-title placeholder-glow mb-3">
                  <span className="placeholder col-6"></span>
                </h5>
                <p className="card-text placeholder-glow">
                  <span className="placeholder col-7"></span>
                  <span className="placeholder col-4"></span>
                  <span className="placeholder col-6"></span>
                  <span className="placeholder col-4"></span>
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      );
    }

    // Show nothing if no data and not loading
    if (!extraFacilityData || !Array.isArray(extraFacilityData) || extraFacilityData.length === 0)
      return null;

    // Derive title for the section
    const sectionTitle = extraFacilityData.length > 0 ? (extraFacilityData[0].FacilityGroupName || `${title} Facilities`) : `${title} Facilities`;

    return (
      <div className="container-fluid py-5">
        <h4 className="text-center mb-4 text-danger">{sectionTitle}</h4>
        <div className="row g-4">
          {extraFacilityData.map((facility, index) => {
            const {
              Facility_ID,
              Facility_Name,
              Facility_Description,
              Facility_Image = [],
              Facility_DesImage = null,
              FacilityGroupName = "", // Might be useful if available
            } = facility;
            let parsedDesc = Facility_Description || "";
            try {
              if (typeof parsedDesc === "string" && parsedDesc.startsWith('"')) {
                parsedDesc = JSON.parse(parsedDesc);
              }
            } catch {
              parsedDesc = "No description available.";
            }
            return (
              <div className="col-md-6 col-lg-3" key={`facility-card-${Facility_ID || index}`}>
                <Card
                  className="shadow border-0 h-100"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(`/Facility/${encodeURIComponent(Facility_Name)}/${Facility_ID}`, {
                      state: {
                        title: Facility_Name,
                        id: Facility_ID,
                        desc: parsedDesc,
                        images: Facility_Image || [],
                        Description: Facility_Description,
                        Facility_DesImage: Facility_DesImage || "",
                        branch: [],
                        FacilityGroupID: Facility_ID,
                      },
                    })
                  }
                >
                  {Facility_Image.length > 0 ? (
                    <div
                      id={`carousel-${Facility_ID || index}`}
                      className="carousel slide object-fit-fill"
                      data-bs-ride="carousel"
                    >
                      <div className="carousel-inner">
                        {Facility_Image.map((img, imgIndex) => (
                          <div
                            key={`img-${imgIndex}`}
                            className={`carousel-item ${imgIndex === 0 ? "active" : ""}`}
                          >
                            <LazyLoadImage
                              src={img}
                              alt={`Facility image ${imgIndex + 1}`}
                              effect="blur"
                              className="d-block w-100"
                              style={{ 
                                height: "200px", 
                                objectFit: "cover", 
                                objectPosition: "center",
                                width: "100%",
                                maxHeight: "100%"
                              }}
                            />
                          </div>
                        ))}
                      </div>
                      {Facility_Image.length > 1 && (
                        <>
                          <button
                            className="carousel-control-prev"
                            type="button"
                            data-bs-target={`#carousel-${Facility_ID || index}`}
                            data-bs-slide="prev"
                          >
                            <span className="carousel-control-prev-icon" />
                            <span className="visually-hidden">Previous</span>
                          </button>
                          <button
                            className="carousel-control-next"
                            type="button"
                            data-bs-target={`#carousel-${Facility_ID || index}`}
                            data-bs-slide="next"
                          >
                            <span className="carousel-control-next-icon" />
                            <span className="visually-hidden">Next</span>
                          </button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div
                      className="card-img-top bg-secondary d-flex align-items-center justify-content-center text-white"
                      style={{ height: "250px" }}
                    >
                      No Image Available
                    </div>
                  )}
                  <Card.Body>
                    <Card.Title className="text-primary">{Facility_Name || "Unnamed Facility"}</Card.Title>
                  </Card.Body>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid p-0">
      {/* Hero Carousel */}
      <div 
        className="position-relative" 
        style={{ 
          height: getResponsiveHeight(), 
          overflow: "hidden",
          width: "100%"
        }}
      >
        <div
          id="heroCarousel"
          className="carousel slide h-100 w-100"
          data-bs-ride="carousel"
          data-bs-interval="3000"
        >
          <div className="carousel-inner h-100 w-100">
            {images.map((img, index) => (
              <div
                className={`carousel-item h-100 w-100 ${index === 0 ? "active" : ""}`}
                key={`carousel-img-${index}`}
              >
                <LazyLoadImage
                  src={img}
                  alt={`${title} ${index + 1}`}
                  effect="blur"
                  className="d-inline w-100 h-100"
                  style={{
                    height: "100%",
                    width: "100vw",
                    objectPosition: "center",
                    minHeight: "100%",
                    minWidth: "100%"
                  }}
                />
                {index === 0 && (
                  <div
                    className="position-absolute top-50 start-50 translate-middle text-white text-center px-3"
                    style={{
                      background: "rgba(0,0,0,0.5)",
                      padding: "1.5rem",
                      borderRadius: "1rem",
                      zIndex: 10
                    }}
                  >
                    <h1 className="fw-bold">{title}</h1>
                    <p className="fs-5">{desc}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          {images.length > 1 && (
            <>
              <button
                className="carousel-control-prev"
                type="button"
                data-bs-target="#heroCarousel"
                data-bs-slide="prev"
              >
                <span className="carousel-control-prev-icon" />
              </button>
              <button
                className="carousel-control-next"
                type="button"
                data-bs-target="#heroCarousel"
                data-bs-slide="next"
              >
                <span className="carousel-control-next-icon" />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="container py-5">
        {error && (
          <div className="alert alert-danger text-center">{error}</div>
        )}
        {desc2 && (
          <p style={{ fontSize: "16px", lineHeight: "1.8em", textAlign: "center" }}>
            {desc2}
          </p>
        )}

        {/* Render Extra Facility Cards with Loading */}
        {renderExtraFacilityCard()}

        {branch.length > 0 && (
          <>
            <h5 className="mt-5">Branches & Timings</h5>
            <ul className="list-group">
              {branch.map((b, i) => (
                <li className="list-group-item" key={`branch-${i}`}>
                  <strong>{b.branchName}</strong>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
} 